import { useQuery } from '@apollo/client'
import { BigNumber } from '@ethersproject/bignumber'
import { useWeb3React } from '@web3-react/core'
import { Token } from '@x-swap-protocol/sdk-core'
import { FeeAmount, Pool, Position } from '@x-swap-protocol/v3-sdk'
import gql from 'graphql-tag'
import { useMemo } from 'react'
import { PositionDetails } from 'types/position'

import { useTheGraphClient } from './useTheGraphClient'

const ZERO = BigNumber.from(0)
const positionDetailsCache: Record<string, PositionDetails> = {}

const V3_POSITION_FIELDS = gql`
  fragment V3PositionFields on Position {
    id
    owner
    liquidity
    feeGrowthInside0LastX128
    feeGrowthInside1LastX128
    token0 {
      id
      symbol
      name
      decimals
      derivedETH
    }
    token1 {
      id
      symbol
      name
      decimals
      derivedETH
    }
    tickLower {
      tickIdx
    }
    tickUpper {
      tickIdx
    }
    pool {
      id
      feeTier
      liquidity
      sqrtPrice
      tick
    }
  }
`

const USER_V3_POSITIONS_QUERY = gql`
  ${V3_POSITION_FIELDS}
  query UserV3Positions($owner: Bytes!) {
    positions(first: 1000, where: { owner: $owner }, orderBy: id, orderDirection: desc, subgraphError: allow) {
      ...V3PositionFields
    }
    bundles(first: 1, subgraphError: allow) {
      ethPriceUSD
    }
  }
`

const V3_POSITION_QUERY = gql`
  ${V3_POSITION_FIELDS}
  query V3Position($id: ID!) {
    position(id: $id, subgraphError: allow) {
      ...V3PositionFields
    }
    bundles(first: 1, subgraphError: allow) {
      ethPriceUSD
    }
  }
`

interface SubgraphToken {
  id: string
  symbol: string
  name: string
  decimals: string
  derivedETH: string
}

interface SubgraphPosition {
  id: string
  owner: string
  liquidity: string
  feeGrowthInside0LastX128: string
  feeGrowthInside1LastX128: string
  token0: SubgraphToken
  token1: SubgraphToken
  tickLower: {
    tickIdx: string
  }
  tickUpper: {
    tickIdx: string
  }
  pool: {
    id: string
    feeTier: string
    liquidity: string
    sqrtPrice: string
    tick?: string | null
  }
}

interface Bundle {
  ethPriceUSD: string
}

interface UserV3PositionsResponse {
  positions: SubgraphPosition[]
  bundles: Bundle[]
}

interface V3PositionResponse {
  position?: SubgraphPosition | null
  bundles: Bundle[]
}

function toPositionDetails(position: SubgraphPosition): PositionDetails {
  return {
    nonce: ZERO,
    tokenId: BigNumber.from(position.id),
    operator: '',
    token0: position.token0.id,
    token1: position.token1.id,
    fee: Number(position.pool.feeTier),
    tickLower: Number(position.tickLower.tickIdx),
    tickUpper: Number(position.tickUpper.tickIdx),
    liquidity: BigNumber.from(position.liquidity),
    feeGrowthInside0LastX128: BigNumber.from(position.feeGrowthInside0LastX128),
    feeGrowthInside1LastX128: BigNumber.from(position.feeGrowthInside1LastX128),
    tokensOwed0: ZERO,
    tokensOwed1: ZERO,
    usdValue: undefined,
  }
}

function getSubgraphToken(token: SubgraphToken, chainId: number | undefined): Token | undefined {
  if (!chainId) return undefined
  return new Token(chainId, token.id, Number(token.decimals), token.symbol, token.name)
}

function withUSDValues(
  positions: SubgraphPosition[],
  chainId: number | undefined,
  ethPriceUSD: number | undefined
): PositionDetails[] {
  return positions.map((position) => {
    const positionDetails = toPositionDetails(position)
    if (typeof ethPriceUSD === 'number' && Number.isFinite(ethPriceUSD)) {
      positionDetails.usdValue = getSubgraphPositionUSDValueWithEthPrice(position, chainId, ethPriceUSD)
    }
    positionDetailsCache[position.id] = positionDetails
    return positionDetails
  })
}

function getSubgraphPositionUSDValueWithEthPrice(
  position: SubgraphPosition,
  chainId: number | undefined,
  ethPriceUSD: number
): number | undefined {
  const token0 = getSubgraphToken(position.token0, chainId)
  const token1 = getSubgraphToken(position.token1, chainId)
  const tick = position.pool.tick

  if (!token0 || !token1 || !tick || !position.pool.sqrtPrice || !position.pool.liquidity) return undefined

  try {
    const pool = new Pool(
      token0,
      token1,
      Number(position.pool.feeTier) as FeeAmount,
      position.pool.sqrtPrice,
      position.pool.liquidity,
      Number(tick)
    )
    const derivedPosition = new Position({
      pool,
      liquidity: position.liquidity,
      tickLower: Number(position.tickLower.tickIdx),
      tickUpper: Number(position.tickUpper.tickIdx),
    })
    const token0PriceUSD = Number(position.token0.derivedETH) * ethPriceUSD
    const token1PriceUSD = Number(position.token1.derivedETH) * ethPriceUSD

    return (
      Number(derivedPosition.amount0.toExact()) * token0PriceUSD +
      Number(derivedPosition.amount1.toExact()) * token1PriceUSD
    )
  } catch (error) {
    return undefined
  }
}

export function useSubgraphV3Positions(account: string | null | undefined): {
  loading: boolean
  error: boolean
  positions?: PositionDetails[]
} {
  const { chainId } = useWeb3React()
  const client = useTheGraphClient()
  const owner = account?.toLowerCase()

  const { data, loading, error } = useQuery<UserV3PositionsResponse>(USER_V3_POSITIONS_QUERY, {
    client,
    variables: owner ? { owner } : undefined,
    skip: !owner,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  const positions = useMemo(() => {
    if (!data?.positions) return undefined
    return withUSDValues(data.positions, chainId, Number(data.bundles?.[0]?.ethPriceUSD))
  }, [chainId, data?.bundles, data?.positions])

  return {
    loading,
    error: Boolean(error),
    positions,
  }
}

export function useSubgraphV3Position(tokenId: BigNumber | undefined): {
  loading: boolean
  error: boolean
  position?: PositionDetails
} {
  const { chainId } = useWeb3React()
  const client = useTheGraphClient()
  const id = tokenId?.toString()
  const cachedPositionDetails = id ? positionDetailsCache[id] : undefined
  const cachedPosition = useMemo(() => {
    if (!id) return null

    return client.cache.readFragment<SubgraphPosition>({
      id: client.cache.identify({ __typename: 'Position', id }),
      fragment: V3_POSITION_FIELDS,
    })
  }, [client, id])

  const { data, loading, error } = useQuery<V3PositionResponse>(V3_POSITION_QUERY, {
    client,
    variables: id ? { id } : undefined,
    skip: !id || Boolean(cachedPositionDetails),
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  const position = useMemo(() => {
    if (cachedPositionDetails) return cachedPositionDetails
    const subgraphPosition = cachedPosition ?? data?.position
    if (!subgraphPosition) return undefined
    return withUSDValues([subgraphPosition], chainId, Number(data?.bundles?.[0]?.ethPriceUSD))[0]
  }, [cachedPosition, cachedPositionDetails, chainId, data?.bundles, data?.position])

  return {
    loading,
    error: Boolean(error),
    position,
  }
}
